import React, { useCallback, useRef, useState } from 'react'

import {
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ArrowDownIcon, ArrowUpIcon, InfoIcon } from 'lucide-react'
import { runInAction } from 'mobx'
import { classList } from 'react-classlist-helper'

import Draggable from '~/client/src/shared/components/LazyDraggable'
import SelectButton from '~/client/src/shared/components/SelectButton/SelectButton'
import SelectionPopUp, {
  SelectionPopUpOption,
} from '~/client/src/shared/components/SelectionPopUp/SelectionPopUp'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { isEventFromOwnDom } from '~/client/src/shared/utils/isEventFromOwnDom'

import Localization from '../../localization/LocalizationManager'
import LocationBase from '../../models/LocationObjects/LocationBase'
import { NOOP } from '../../utils/noop'
import { DASH } from '../../utils/usefulStrings'
import {
  GroupConnectorLines,
  GroupHeader,
  GroupHeaderTrigger,
  GroupIndent,
} from '../GroupHeader'
import { Checkbox } from '../shadcn-ui/checkbox'
import SitemapAttributeIconComponent from '../SitemapAttributeTag/SitemapAttributeIcon'
import TooltipWrapper from '../TooltipWrapper/TooltipWrapper'
import { ILWFCCategory, ILWFCColumn } from './VirtualListHelpers'

import './ListWithFixedColumns.scss'
import './VirtualMultiGrid.scss'

export interface IDataRow {
  data: { [key: string]: any }
  category?: ILWFCCategory
  height?: number
  level?: number
}

const DEFAULT_ROW_HEIGHT = 40
const CATEGORY_ROW_HEIGHT = 32
const DEFAULT_COLUMN_WIDTH = 150
const CELL_MIN_WIDTH = 45
// Ancestor chevron centre = band pl + depth * 24 + 8 (half the 16px chevron).
const CHEVRON_HALF_PX = 8
const BAND_PL_PX = 16
const TRAILING_COUNT_REGEX = /\((\d+)\)\s*$/

// Category rows carry their count baked into label strings (e.g. `Area (13)` or a ` (13)`
// suffix). Split it back out so the band renders the label and count through the shared slots.
export function getCategoryLabelParts(
  category: ILWFCCategory | undefined,
  categoryId: string,
): { label: string; count?: string } {
  const countFromSuffix = category?.countLabel?.match(TRAILING_COUNT_REGEX)?.[1]
  if (category?.shortCategoryLabel) {
    return {
      label: category.shortCategoryLabel,
      count:
        countFromSuffix ??
        category.categoryLabel?.match(TRAILING_COUNT_REGEX)?.[1],
    }
  }
  return {
    label: category?.categoryLabel || categoryId,
    count: countFromSuffix,
  }
}

interface VirtualTableProps {
  rows: IDataRow[]
  columns: ILWFCColumn[]

  className?: string
  columnHeaderClassesMap?: {
    [columnKey: string]: string
  }
  columnCellClassesMap?: {
    [columnKey: string]: string
  }
  columnCategoryCellClassesMap?: {
    [columnKey: string]: string
  }
  fixedColumnsCount?: number
  collapsedCategories?: Map<string, boolean>
  sortedColumnKey?: string
  sortingOrder?: SortOrder
  columnsWidthState?: Map<string, number>
  disabledResizeColumns?: string[]

  cellRenderer?: (
    columnKey: string,
    value: any,
    rowData?: any,
  ) => React.ReactNode
  categoryCellRenderer?: (
    rowData: any,
    column: ILWFCColumn,
    columnIndex: number,
    isEdge: boolean,
  ) => React.ReactNode
  headerRenderer?: (columnKey: string, value: any) => React.ReactNode
  onCellClick?: (rowData: any, columnKey: string) => void
  onCategoryCollapsingToggle?: (categoryId: string) => void
  onColumnSort?: (columnKey: string) => void
  selectAll?: () => void
  resetAll?: () => void
  onCategoryCheckboxToggle?: (category: any) => void
  measureRowHeight?: boolean
  noHeader?: boolean
  dataCellClass?: string
  highlightOpenItemRow?: boolean
  selectedItemId?: string
  categoryLabelColumnIndex?: number
  fullWidthCategoryLabel?: boolean
  onColumnResize?: (columnKey: string, width: number) => void
}

export default function VirtualMultiGrid({
  rows: dataRows,
  columns: dataColumns,
  className,
  columnHeaderClassesMap,
  columnCellClassesMap,
  columnCategoryCellClassesMap,
  fixedColumnsCount = 1,
  disabledResizeColumns,
  cellRenderer,
  categoryCellRenderer,
  headerRenderer,
  onCellClick,
  collapsedCategories,
  onCategoryCollapsingToggle,
  onColumnSort = NOOP,
  sortedColumnKey,
  sortingOrder = SortOrder.DEFAULT,
  columnsWidthState = new Map(),
  selectAll = NOOP,
  resetAll = NOOP,
  onCategoryCheckboxToggle = NOOP,
  noHeader = false,
  measureRowHeight = false,
  dataCellClass,
  highlightOpenItemRow = false,
  selectedItemId,
  categoryLabelColumnIndex = 1,
  fullWidthCategoryLabel = false,
  onColumnResize = NOOP,
}: VirtualTableProps) {
  const parentScrollRef = useRef<HTMLDivElement>(null)
  const [shouldShowSelectionPopup, setShouldShowSelectionPopup] =
    useState(false)
  const shownColumns = dataColumns.filter(column => !column.isHidden)
  const selectionPopUpOptions: SelectionPopUpOption[] = React.useMemo(
    () => [
      {
        label: Localization.translator.selectAll_items,
        onClick: () => {
          selectAll()
          setShouldShowSelectionPopup(false)
        },
      },
      {
        label: Localization.translator.clear,
        onClick: () => {
          resetAll()
          setShouldShowSelectionPopup(false)
        },
      },
    ],
    [selectAll, resetAll, Localization.state.currentLanguage],
  )

  const handleColumnResize = useCallback(
    ({ columnIndex, deltaX }) => {
      const column = shownColumns[columnIndex]
      if (!column) return

      const currentWidth =
        columnsWidthState.get(column.dataKey) ||
        column.width ||
        DEFAULT_COLUMN_WIDTH
      const newWidth = Math.max(CELL_MIN_WIDTH, currentWidth + deltaX)
      runInAction(() => {
        columnsWidthState.set(column.dataKey, newWidth)
      })
      onColumnResize?.(column.dataKey, newWidth)
    },
    [columnsWidthState, shownColumns, onColumnResize],
  )

  const toggleSelectionPopup = useCallback(() => {
    setShouldShowSelectionPopup(!shouldShowSelectionPopup)
  }, [shouldShowSelectionPopup])

  const [expanded, setExpanded] = React.useState<ExpandedState>({})

  React.useEffect(() => {
    if (collapsedCategories) {
      const newExpanded: ExpandedState = {}
      collapsedCategories.forEach((isCollapsed, categoryId) => {
        newExpanded[categoryId] = !isCollapsed
      })
      setExpanded(newExpanded)
    }
  }, [collapsedCategories])

  const data = React.useMemo(() => {
    if (!dataRows || !Array.isArray(dataRows)) {
      console.warn('Invalid attachmentRows:', dataRows)
      return []
    }

    const result = []
    let currentCategoryId = null
    let skipDataRows = false

    dataRows
      .filter(row => row && (row.data || row.category))
      .forEach((row, index) => {
        if (row.category) {
          currentCategoryId = row.category.categoryId
          skipDataRows = collapsedCategories?.get(currentCategoryId) ?? false

          result.push({
            id: `category-${row.category.categoryId}`,
            isCategory: true,
            category: row.category,
            data: row.data || {},
            level: row.level || 0,
            originalIndex: index,
            ...row.category,
          })
        } else if (!skipDataRows) {
          const baseId = row.data.id || `row-${index}`
          const uniqueId = currentCategoryId
            ? `${currentCategoryId}:${baseId}`
            : baseId
          result.push({
            id: uniqueId,
            isCategory: false,
            originalIndex: index,
            level: row.level || 0,
            parentCategoryId: currentCategoryId,
            ...row.data,
            _originalRow: row,
            _originalRowData: row.data,
          })
        }
      })

    return result
  }, [dataRows, collapsedCategories?.size])

  const columns: ColumnDef<any>[] = React.useMemo(() => {
    if (!shownColumns || !Array.isArray(shownColumns)) {
      console.warn('Invalid attachmentColumns:', shownColumns)
      return []
    }

    const validColumns = shownColumns.filter(col => col && col.dataKey)

    return validColumns.map((col, index) => {
      const isFixed = col.isFixed || index < fixedColumnsCount
      const widthFromState = columnsWidthState.get(col.dataKey)
      return {
        id: col.dataKey,
        accessorKey: col.dataKey,
        header: col.label || col.dataKey,
        size: widthFromState || col.width || DEFAULT_COLUMN_WIDTH,
        meta: {
          fixed: isFixed,
          dataKey: col.dataKey,
        },
        accessorFn: col.accessorFn,
        cell: ({ getValue, row }) => {
          const rowData = row.original

          if (rowData.isCategory) {
            const isEdge = col.isFixed && index === fixedColumnsCount - 1
            return renderCategoryCell(rowData, col, index, isEdge)
          }

          try {
            const value = getValue()

            if (cellRenderer) {
              return cellRenderer(col.dataKey, value, rowData)
            }

            if (value === undefined || value === null) {
              return DASH
            }
            return String(value)
          } catch (error) {
            console.warn(`Error rendering cell for ${col.dataKey}:`, error)
            return DASH
          }
        },
      }
    })
  }, [
    dataColumns,
    fixedColumnsCount,
    columnsWidthState,
    cellRenderer,
    collapsedCategories,
  ])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getRowCanExpand: row => !!row.subRows?.length,
    onExpandedChange: setExpanded,
    state: {
      expanded,
    },
  })

  const rows = React.useMemo(() => {
    return table.getRowModel().rows
  }, [table.getRowModel().rows])

  const { fixedColumns, scrollableColumns } = React.useMemo(() => {
    const allColumns = table.getAllLeafColumns()
    return {
      fixedColumns: allColumns.filter(
        (col, index) => index < fixedColumnsCount,
      ),
      scrollableColumns: allColumns.filter(
        (col, index) => index >= fixedColumnsCount,
      ),
    }
  }, [table, fixedColumnsCount, dataColumns, columns])
  const fixedWidth = React.useMemo(() => {
    return fixedColumns.reduce(
      (sum, col) => sum + (col.getSize() || DEFAULT_COLUMN_WIDTH),
      0,
    )
  }, [fixedColumns])
  const scrollableWidth = React.useMemo(() => {
    return scrollableColumns.reduce(
      (sum, col) => sum + (col.getSize() || DEFAULT_COLUMN_WIDTH),
      0,
    )
  }, [scrollableColumns])
  const checkboxColumnWidth = React.useMemo(() => {
    const checkboxColumn = table
      .getAllLeafColumns()
      .find(column => column.id === 'checkbox')
    return checkboxColumn?.getSize() || 0
  }, [table, columns, fixedColumnsCount])
  // Row checkbox: 12px cell inset + 16px box = right edge at 28px; the label cell starts
  // at the 40px column edge, so band pl-0 is the closest to Figma's 8px checkbox→chevron gap.
  const hasLeadingCheckboxColumn =
    shownColumns[categoryLabelColumnIndex - 1]?.dataKey === 'checkbox'

  const handleCellClick = useCallback(
    (row: any, columnKey: string) => {
      if (row.isCategory) {
        if (onCategoryCollapsingToggle) {
          onCategoryCollapsingToggle(row.categoryId)
        }
      } else {
        if (onCellClick) {
          const originalRowData = row._originalRowData || row
          onCellClick(originalRowData, columnKey)
        }
      }
    },
    [onCellClick, onCategoryCollapsingToggle],
  )

  const renderCategoryCell = useCallback(
    (
      rowData: any,
      column: ILWFCColumn,
      columnIndex: number,
      isEdge: boolean,
    ) => {
      const { category, categoryId, data, level } = rowData
      const isCollapsed = collapsedCategories?.get(categoryId) ?? false
      const shouldShowCategoryLabel = columnIndex === categoryLabelColumnIndex
      const { dataKey } = column

      const location = data as LocationBase
      const CustomIcon: React.ComponentType<any> = data?.icon
      const customIconProps = data?.iconProps
      const infoTooltip = data?.infoTooltip
      const labelNode: React.ReactNode = data?.labelNode
      const additionalClassName = columnCategoryCellClassesMap?.[dataKey]

      let cellContent = categoryCellRenderer?.(
        rowData,
        column,
        columnIndex,
        isEdge,
      )

      if (shouldShowCategoryLabel) {
        const { label, count } = getCategoryLabelParts(category, categoryId)
        // Per-instance color lives on the icon only; the label keeps the band's text color.
        const labelIcon = CustomIcon ? (
          <CustomIcon
            {...customIconProps}
            className={classList({
              'tw:shrink-0': true,
              [customIconProps?.className || '']: !!customIconProps?.className,
            })}
          />
        ) : location?.iconName ? (
          <SitemapAttributeIconComponent dataObject={location} />
        ) : undefined

        // `w-max` lets the band overflow the label column across the row's empty
        // category cells, which share the same sidebar fill.
        cellContent = (
          <GroupHeader
            className={classList({
              'tw:relative tw:w-max tw:min-w-full tw:border-b-0': true,
              'tw:pl-0': hasLeadingCheckboxColumn,
            })}
          >
            <GroupConnectorLines
              depth={level || 0}
              leftOffsetPx={
                (hasLeadingCheckboxColumn ? 0 : BAND_PL_PX) + CHEVRON_HALF_PX
              }
            />
            <GroupHeaderTrigger
              expanded={!isCollapsed}
              onClick={e => {
                // The cell also toggles on click; stop here so one click toggles once.
                e.stopPropagation()
                onCategoryCollapsingToggle?.(categoryId)
              }}
              ariaLabel={label}
              className="tw:flex-none"
              leading={<GroupIndent depth={level || 0} />}
              labelIcon={labelNode ? undefined : labelIcon}
              label={labelNode ?? label}
              count={count}
            />
            {infoTooltip && (
              <TooltipWrapper
                content={infoTooltip}
                className="tw:flex tw:shrink-0 tw:items-center"
              >
                <InfoIcon className="tw:size-3 tw:shrink-0 tw:text-muted-foreground" />
              </TooltipWrapper>
            )}
          </GroupHeader>
        )
      } else if (dataKey === 'checkbox') {
        cellContent = (
          <div
            className="tw:flex"
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <Checkbox
              checked={category?.isChecked || false}
              indeterminate={category?.isPartialChecked || false}
              onCheckedChange={() => onCategoryCheckboxToggle(category)}
            />
          </div>
        )
      }

      const cellClassNames = classList({
        'tw:flex tw:h-full tw:w-full tw:items-center tw:border-0 tw:border-b tw:border-solid tw:border-input-soft tw:bg-sidebar tw:text-foreground': true,
        'cell category-cell tw:select-none tw:cursor-pointer': true,
        'tw:border-r tw:border-r-border': isEdge,
        // Same 12px inset as the row checkbox cell (`__data-cell--value`), so the band
        // checkbox stays on the row checkbox x even outside `.multi-grid-container`.
        'tw:px-3': dataKey === 'checkbox',
        'category-cell-name': shouldShowCategoryLabel,
        'no-padding': columnIndex === categoryLabelColumnIndex,
        [additionalClassName]: !!additionalClassName,
      })

      return (
        <div
          className={cellClassNames}
          onClick={e => {
            e.stopPropagation()
            handleCellClick(rowData, column.dataKey)
          }}
        >
          {cellContent}
        </div>
      )
    },
    [
      collapsedCategories,
      columnCategoryCellClassesMap,
      categoryCellRenderer,
      handleCellClick,
      onCategoryCheckboxToggle,
      onCategoryCollapsingToggle,
      categoryLabelColumnIndex,
      hasLeadingCheckboxColumn,
    ],
  )

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentScrollRef.current,
    estimateSize: index =>
      rows[index]?.original?.isCategory
        ? CATEGORY_ROW_HEIGHT
        : DEFAULT_ROW_HEIGHT,
    getItemKey: index => rows[index]?.original?.id ?? index,
    overscan: 5,
  })

  function shouldHightlightRow(isCategory: any, rowData: any) {
    return (
      !isCategory &&
      (typeof rowData['checkbox'] === 'boolean'
        ? rowData['checkbox']
        : rowData['checkbox']?.value ||
          (highlightOpenItemRow && rowData['id'] === selectedItemId))
    )
  }

  if (!shownColumns || shownColumns.length === 0 || columns.length === 0) {
    return (
      <div className="virtual-multi-grid__empty-state virtual-multi-grid__empty-state--no-columns">
        <div>{Localization.translator.noColumnsDefined}</div>
      </div>
    )
  }

  return (
    <div
      ref={parentScrollRef}
      className={classList({
        'virtual-multi-grid tw:overflow-auto tw:overscroll-none': true,
        [className]: !!className,
      })}
    >
      <div
        className="virtual-multi-grid__content-container"
        style={{
          height:
            rowVirtualizer.getTotalSize() + (noHeader ? 0 : DEFAULT_ROW_HEIGHT),
          minWidth: `${fixedWidth + scrollableWidth}px`,
        }}
      >
        {!noHeader && (
          <div
            className="virtual-multi-grid__header-row bb-palette-grey"
            style={{
              minWidth: `${fixedWidth + scrollableWidth}px`,
            }}
          >
            {fixedColumns.length > 0 && (
              <div className="virtual-multi-grid__header-fixed bg-white full-height">
                {fixedColumns.map((column, index) => {
                  const isCheckboxColumn = column.columnDef.id === 'checkbox'
                  const isColumnSorted = sortedColumnKey === column.id
                  const handler = isCheckboxColumn
                    ? NOOP
                    : () => onColumnSort(column.id)
                  const additionalClassName =
                    columnHeaderClassesMap?.[column.id]
                  const isResizeDisabled = disabledResizeColumns?.includes(
                    column.id,
                  )

                  return (
                    <div
                      key={column.id}
                      className={classList({
                        'virtual-multi-grid__header-cell relative': true,
                        'virtual-multi-grid__header-cell--sortable':
                          !isCheckboxColumn,
                        'virtual-multi-grid__header-cell--default':
                          isCheckboxColumn,
                        'sorted bold white': isColumnSorted,
                        pointer: !isCheckboxColumn,
                        'x-center': isCheckboxColumn,
                        [additionalClassName]: !!additionalClassName,
                      })}
                      style={{
                        width: column.getSize() || DEFAULT_COLUMN_WIDTH,
                      }}
                    >
                      {isCheckboxColumn ? (
                        <div className="tw:flex tw:items-center tw:justify-center px4">
                          <SelectButton onClick={toggleSelectionPopup} />
                        </div>
                      ) : (
                        <div
                          onClick={handler}
                          className="full-width full-height"
                        >
                          <div className="row y-center full-height text medium-bold grey-light large ellipsis">
                            {headerRenderer?.(
                              column.id,
                              column.columnDef.header,
                            ) || column.columnDef.header}
                            {isColumnSorted && (
                              <span className="tw:flex-none! tw:pl-0.5">
                                {sortingOrder === SortOrder.ASC ? (
                                  <ArrowDownIcon size={8} />
                                ) : (
                                  <ArrowUpIcon size={8} />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {!isResizeDisabled && (
                        <Draggable
                          axis="x"
                          defaultClassName="DragHandle"
                          defaultClassNameDragging="DragHandleActive"
                          onStop={(_, data) =>
                            handleColumnResize({
                              columnIndex: index,
                              deltaX: data.x,
                            })
                          }
                          position={{
                            x: 0,
                            y: 0,
                          }}
                        >
                          <div className="resize-cursor resize-cursor-holder" />
                        </Draggable>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {scrollableColumns.length > 0 && (
              <div className="virtual-multi-grid__header-scrollable bg-white full-height">
                {scrollableColumns.map((column, index) => {
                  const isCheckboxColumn = column.columnDef.id === 'checkbox'
                  const isColumnSorted = sortedColumnKey === column.id
                  const handler = isCheckboxColumn
                    ? NOOP
                    : () => onColumnSort(column.id)
                  const actualIndex = fixedColumnsCount + index
                  const additionalClassName =
                    columnHeaderClassesMap?.[column.id]
                  const isResizeDisabled = disabledResizeColumns?.includes(
                    column.id,
                  )

                  return (
                    <div
                      key={column.id}
                      className={classList({
                        'virtual-multi-grid__header-cell relative': true,
                        'virtual-multi-grid__header-cell--sortable':
                          !isCheckboxColumn,
                        'virtual-multi-grid__header-cell--default':
                          isCheckboxColumn,
                        'sorted bold white': isColumnSorted,
                        pointer: !isCheckboxColumn,
                        'x-center': isCheckboxColumn,
                        [additionalClassName]: !!additionalClassName,
                      })}
                      style={{
                        width: column.getSize() || DEFAULT_COLUMN_WIDTH,
                        flexShrink: 0,
                      }}
                    >
                      {isCheckboxColumn ? (
                        <div className="tw:flex tw:items-center tw:justify-center px4">
                          <SelectButton onClick={toggleSelectionPopup} />
                        </div>
                      ) : (
                        <div
                          onClick={handler}
                          className="full-width full-height"
                        >
                          <div className="row y-center full-height text medium-bold grey-light large ellipsis">
                            {headerRenderer?.(
                              column.id,
                              column.columnDef.header,
                            ) || column.columnDef.header}
                            {isColumnSorted && (
                              <span className="tw:flex-none! tw:pl-0.5">
                                {sortingOrder === SortOrder.ASC ? (
                                  <ArrowDownIcon size={8} />
                                ) : (
                                  <ArrowUpIcon size={8} />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {!isResizeDisabled && (
                        <Draggable
                          axis="x"
                          defaultClassName="DragHandle"
                          defaultClassNameDragging="DragHandleActive"
                          onStop={(_, data) =>
                            handleColumnResize({
                              columnIndex: actualIndex,
                              deltaX: data.x,
                            })
                          }
                          position={{
                            x: 0,
                            y: 0,
                          }}
                        >
                          <div className="resize-cursor resize-cursor-holder" />
                        </Draggable>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {rowVirtualizer.getVirtualItems().map(virtualRow => {
          const row = rows[virtualRow.index]
          const rowData = row.original
          const isCategory = rowData.isCategory

          return (
            <div
              key={row.id}
              data-index={virtualRow.index}
              ref={measureRowHeight ? rowVirtualizer.measureElement : undefined}
              className={classList({
                'virtual-multi-grid__data-row bg-white': true,
                'virtual-multi-grid__data-row--clickable': !!onCellClick,
              })}
              style={{
                transform: `translateY(${
                  virtualRow.start + (noHeader ? 0 : DEFAULT_ROW_HEIGHT)
                }px)`,
                minHeight: `${virtualRow.size}px`,
                height: measureRowHeight ? undefined : `${virtualRow.size}px`,
                minWidth: `${fixedWidth + scrollableWidth}px`,
              }}
            >
              {fixedColumns.length > 0 && (
                <div className="virtual-multi-grid__data-fixed full-height">
                  {fixedColumns.map((column, fixedColumnIndex) => {
                    const cell = row
                      .getVisibleCells()
                      .find(c => c.column.id === column.id)
                    const isCheckboxColumn = column.columnDef.id === 'checkbox'
                    const isCategoryLabelCell =
                      isCategory &&
                      fullWidthCategoryLabel &&
                      fixedColumnIndex === categoryLabelColumnIndex
                    const shouldHideCategoryCell =
                      isCategory &&
                      fullWidthCategoryLabel &&
                      !isCategoryLabelCell &&
                      !isCheckboxColumn

                    const additionalClassName =
                      columnCellClassesMap?.[column.id]

                    if (shouldHideCategoryCell) {
                      return null
                    }

                    return (
                      <div
                        key={column.id}
                        className={classList({
                          'virtual-multi-grid__data-cell': true,
                          'virtual-multi-grid__data-cell--category': isCategory,
                          'virtual-multi-grid__data-cell--value bb-light-input-border':
                            !isCategory,
                          'bg-white': true,
                          'br-light-cool-grey':
                            !isCheckboxColumn && !isCategory,
                          'x-center pointer': isCheckboxColumn,
                          'gant-selection': shouldHightlightRow(
                            isCategory,
                            rowData,
                          ),
                          [additionalClassName]: !!additionalClassName,
                        })}
                        style={{
                          width: isCategoryLabelCell
                            ? fixedWidth + scrollableWidth - checkboxColumnWidth
                            : column.getSize() || DEFAULT_COLUMN_WIDTH,
                          height: measureRowHeight
                            ? undefined
                            : `${virtualRow.size}px`,
                          minHeight: `${virtualRow.size}px`,
                        }}
                        onClick={e => {
                          if (!isEventFromOwnDom(e)) return
                          if (isCategory) {
                            e.stopPropagation()
                            return
                          }
                          if (isCheckboxColumn && !isCategory) {
                            e.stopPropagation()
                            handleCellClick(rowData, column.id)
                            return
                          }
                          handleCellClick(rowData, column.id)
                        }}
                      >
                        {cell ? flexRender(column.columnDef.cell, cell) : ''}
                      </div>
                    )
                  })}
                </div>
              )}

              {scrollableColumns.length > 0 && (
                <div className="virtual-multi-grid__data-scrollable">
                  {scrollableColumns.map((column, scrollableColumnIndex) => {
                    const cell = row
                      .getVisibleCells()
                      .find(c => c.column.id === column.id)
                    const isCheckboxColumn = column.columnDef.id === 'checkbox'
                    const globalColumnIndex =
                      fixedColumnsCount + scrollableColumnIndex
                    const isCategoryLabelCell =
                      isCategory &&
                      fullWidthCategoryLabel &&
                      globalColumnIndex === categoryLabelColumnIndex
                    const shouldHideCategoryCell =
                      isCategory &&
                      fullWidthCategoryLabel &&
                      !isCategoryLabelCell &&
                      !isCheckboxColumn

                    const additionalClassName =
                      columnCellClassesMap?.[column.id]

                    if (shouldHideCategoryCell) {
                      return null
                    }

                    return (
                      <div
                        key={column.id}
                        className={classList({
                          'virtual-multi-grid__data-cell': true,
                          'virtual-multi-grid__data-cell--scrollable': true,
                          'virtual-multi-grid__data-cell--category': isCategory,
                          'virtual-multi-grid__data-cell--value bb-light-input-border':
                            !isCategory,
                          'br-light-cool-grey':
                            !isCheckboxColumn && !isCategory,
                          'x-center': isCheckboxColumn,
                          pointer: isCheckboxColumn,
                          'gant-selection': shouldHightlightRow(
                            isCategory,
                            rowData,
                          ),
                          [additionalClassName]: !!additionalClassName,
                          [dataCellClass]: !!dataCellClass,
                        })}
                        style={{
                          width: isCategoryLabelCell
                            ? fixedWidth + scrollableWidth - checkboxColumnWidth
                            : column.getSize() || DEFAULT_COLUMN_WIDTH,
                          height: measureRowHeight
                            ? undefined
                            : `${virtualRow.size}px`,
                          minHeight: `${virtualRow.size}px`,
                        }}
                        onClick={e => {
                          if (!isEventFromOwnDom(e)) return
                          if (isCategory) {
                            e.stopPropagation()
                            return
                          }
                          if (isCheckboxColumn && !isCategory) {
                            e.stopPropagation()
                            handleCellClick(rowData, column.id)
                            return
                          }
                          handleCellClick(rowData, column.id)
                        }}
                      >
                        {cell ? flexRender(column.columnDef.cell, cell) : ''}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {shouldShowSelectionPopup && (
        <div className="virtual-multi-grid__selection-popup absolute-top left-0 z-index-1000">
          <SelectionPopUp
            isDisplayed={shouldShowSelectionPopup}
            options={selectionPopUpOptions}
          />
        </div>
      )}
    </div>
  )
}
